import { useLocation, Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, Clock as ClockIcon, Calendar, MapPin, Clock, Users, CalendarPlus, Home, Download } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ConfirmationPage() {
  const location = useLocation();
  const { registration, event, isWaitlist } = location.state || {};

  const addToGoogleCalendar = () => {
    if (!event) return;
    
    const startDate = new Date(event.date);
    const endDate = event.end_time ? new Date(event.end_time) : new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
    
    // Format: YYYYMMDDTHHMMSSZ
    const formatGoogleDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const details = registration.confirmation_code 
      ? `Code de confirmation: ${registration.confirmation_code}\n\n${event.description || ''}`
      : event.description || '';
    
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.name)}&dates=${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(event.location)}`;
    
    window.open(googleCalendarUrl, '_blank');
  };

  const downloadICS = () => {
    if (!event) return;
    
    // Use the backend endpoint to generate the .ics file
    const url = registration.confirmation_code && !isWaitlist
      ? `${BACKEND_URL}/api/events/${event.event_id}/calendar.ics?code=${registration.confirmation_code}`
      : `${BACKEND_URL}/api/events/${event.event_id}/calendar.ics`;
    
    window.open(url, '_blank');
  };

  if (!registration || !event) {
    return (
      <div className="min-h-screen bg-[#FFFDF7]">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <div className="text-6xl mb-4">🤔</div>
          <h2 className="font-heading font-bold text-2xl text-[#2D2A26] mb-2">Page non trouvée</h2>
          <p className="text-[#645E55] mb-6">Cette page de confirmation n'est plus disponible.</p>
          <Link to="/">
            <Button className="btn-primary">
              <Home className="w-4 h-4 mr-2" />
              Retour aux événements
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const eventDate = new Date(event.date);

  // Waitlist confirmation
  if (isWaitlist || registration.is_waitlist) {
    const position = registration.confirmation_code?.replace('ATTENTE-', '') || '?';
    
    return (
      <div className="min-h-screen bg-[#FFFDF7]">
        <Header />
        
        <main className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center mb-8 animate-slide-up">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <ClockIcon className="w-12 h-12 text-blue-500" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#2D2A26] mb-3">
              Liste d'attente
            </h1>
            <p className="text-lg text-[#645E55]">
              Merci {registration.first_name}, vous êtes inscrit(e) en liste d'attente !
            </p>
          </div>

          <Card className="border-2 border-[#E5E0D8] rounded-2xl shadow-card mb-6 animate-slide-up stagger-1" data-testid="waitlist-card">
            <CardContent className="p-6 md:p-8">
              {/* Event info */}
              <div className="mb-6">
                <h2 className="font-heading text-xl font-bold text-[#2D2A26] mb-4">
                  {event.name}
                </h2>
                
                <div className="grid gap-3 text-[#645E55]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FFF8E1] rounded-xl flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-[#db3b17]" />
                    </div>
                    <span className="font-medium">
                      {format(eventDate, "EEEE d MMMM yyyy", { locale: fr })}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FFF8E1] rounded-xl flex items-center justify-center">
                      <Clock className="w-5 h-5 text-[#db3b17]" />
                    </div>
                    <span className="font-medium">
                      {format(eventDate, "HH'h'mm", { locale: fr })}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FFF8E1] rounded-xl flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#db3b17]" />
                    </div>
                    <span className="font-medium">{event.location}</span>
                  </div>
                </div>
              </div>

              {/* Position */}
              <div className="bg-[#48b4ff] text-white rounded-2xl p-6 text-center mb-6">
                <p className="text-sm uppercase tracking-wider mb-2 opacity-80">Votre position</p>
                <p className="text-5xl font-bold" data-testid="waitlist-position">
                  #{position}
                </p>
              </div>

              <div className="bg-[#FFF8E1] rounded-xl p-4 text-center">
                <p className="text-[#2D2A26]">
                  <strong>Comment ça marche ?</strong>
                </p>
                <p className="text-sm text-[#645E55] mt-2">
                  Si une place se libère, vous serez automatiquement inscrit(e) et recevrez un email de confirmation avec votre code.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-center animate-slide-up stagger-2">
            <Link to="/">
              <Button className="h-12 btn-primary" data-testid="back-to-events">
                <Home className="w-5 h-5 mr-2" />
                Retour aux événements
              </Button>
            </Link>
          </div>

          <div className="mt-12 text-center text-[#645E55] animate-slide-up stagger-3">
            <p className="text-lg mb-2">Croisons les doigts ! 🤞</p>
            <p className="text-sm">L'équipe Plume et Pion</p>
          </div>
        </main>
      </div>
    );
  }

  // Normal registration confirmation
  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Header />
      
      <main className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-8 animate-slide-up">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#2D2A26] mb-3">
            Inscription confirmée !
          </h1>
          <p className="text-lg text-[#645E55]">
            Merci {registration.first_name}, votre place est réservée !
          </p>
        </div>

        {/* Confirmation card */}
        <Card className="border-2 border-[#E5E0D8] rounded-2xl shadow-card mb-6 animate-slide-up stagger-1" data-testid="confirmation-card">
          <CardContent className="p-6 md:p-8">
            {/* Event info */}
            <div className="mb-6">
              <h2 className="font-heading text-xl font-bold text-[#2D2A26] mb-4">
                {event.name}
              </h2>
              
              <div className="grid gap-3 text-[#645E55]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FFF8E1] rounded-xl flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-[#db3b17]" />
                  </div>
                  <span className="font-medium">
                    {format(eventDate, "EEEE d MMMM yyyy", { locale: fr })}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FFF8E1] rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#db3b17]" />
                  </div>
                  <span className="font-medium">
                    {format(eventDate, "HH'h'mm", { locale: fr })}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FFF8E1] rounded-xl flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#db3b17]" />
                  </div>
                  <span className="font-medium">{event.location}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FFF8E1] rounded-xl flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#db3b17]" />
                  </div>
                  <span className="font-medium">
                    {registration.num_participants} participant{registration.num_participants > 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>

            {/* Confirmation code */}
            <div className="bg-[#48b4ff] text-white rounded-2xl p-6 text-center mb-6">
              <p className="text-sm uppercase tracking-wider mb-2 opacity-80">Code de confirmation</p>
              <p className="text-3xl font-bold tracking-[0.2em]" data-testid="confirmation-code">
                {registration.confirmation_code}
              </p>
            </div>

            <p className="text-sm text-[#645E55] text-center">
              Présentez ce code lors de votre arrivée. Un email de confirmation a été envoyé à{' '}
              <span className="font-medium text-[#2D2A26]">{registration.email}</span>
            </p>
          </CardContent>
        </Card>

        {/* Calendar buttons */}
        <div className="mb-6 animate-slide-up stagger-2">
          <h3 className="font-heading text-lg font-semibold text-[#2D2A26] text-center mb-4">
            Ajouter à mon agenda
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <Button 
              onClick={addToGoogleCalendar}
              variant="outline"
              className="h-14 border-2 border-[#E5E0D8] rounded-xl hover:bg-[#FFF8E1] flex flex-col items-center justify-center"
              data-testid="add-to-google-calendar"
            >
              <svg className="w-5 h-5 mb-1" viewBox="0 0 24 24" fill="none">
                <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5c-1.51 0-2.816.917-3.437 2.25-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z" fill="#4285F4"/>
              </svg>
              <span className="text-sm font-medium">Google Calendar</span>
            </Button>
            <Button 
              onClick={downloadICS}
              variant="outline"
              className="h-14 border-2 border-[#E5E0D8] rounded-xl hover:bg-[#FFF8E1] flex flex-col items-center justify-center"
              data-testid="download-ics"
            >
              <Download className="w-5 h-5 mb-1" />
              <span className="text-sm font-medium">Apple / Outlook</span>
            </Button>
          </div>
        </div>

        {/* Back button */}
        <div className="flex justify-center animate-slide-up stagger-3">
          <Link to="/">
            <Button className="h-12 btn-primary" data-testid="back-to-events">
              <Home className="w-5 h-5 mr-2" />
              Retour aux événements
            </Button>
          </Link>
        </div>

        {/* Thank you message */}
        <div className="mt-12 text-center text-[#645E55] animate-slide-up stagger-4">
          <p className="text-lg mb-2">On a hâte de vous voir ! 🎲</p>
          <p className="text-sm">L'équipe Plume et Pion</p>
        </div>
      </main>
    </div>
  );
}
